// src/components/pipelines/nodes/LogicGateNode.jsx - Logic Gate (AND/OR) Node for React Flow
import React, { useState } from 'react';
import { Handle, Position } from '@xyflow/react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card.tsx';
import { Badge } from '../../ui/badge.tsx';
import { Input } from '../../ui/input.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Button } from '../../ui/button.tsx';
import { Plus, X, Edit2 } from 'lucide-react';
import { useReactFlow } from '@xyflow/react';

/**
 * LogicGateNode - Represents an AND/OR logic gate for conditional branching
 *
 * Node data structure:
 * {
 *   id: 'gate-uuid',
 *   type: 'logicGate',
 *   data: {
 *     gateType: 'AND' | 'OR',
 *     conditions: [{field: 'is_vip', operator: 'equals', value: true}, ...],
 *     name: 'Check VIP Status'
 *   }
 * }
 */
const LogicGateNode = ({ data, selected, id }) => {
  const { gateType = 'AND', conditions = [], name } = data;
  const { updateNodeData } = useReactFlow();

  const [isEditingName, setIsEditingName] = useState(false);
  const [editedName, setEditedName] = useState(name || '');
  const [isAddingCondition, setIsAddingCondition] = useState(false);
  const [newCondition, setNewCondition] = useState({ field: '', operator: 'equals', value: '' });

  const isAND = gateType === 'AND';
  const color = isAND ? 'blue' : 'green';

  // Toggle gate type
  const handleToggleGateType = () => {
    const newType = gateType === 'AND' ? 'OR' : 'AND';
    updateNodeData(id, { ...data, gateType: newType });
  };

  // Save name
  const handleSaveName = () => {
    updateNodeData(id, { ...data, name: editedName.trim() });
    setIsEditingName(false);
  };

  // Add condition
  const handleAddCondition = () => {
    if (newCondition.field && newCondition.value) {
      const updatedConditions = [...conditions, newCondition];
      updateNodeData(id, { ...data, conditions: updatedConditions });
      setNewCondition({ field: '', operator: 'equals', value: '' });
      setIsAddingCondition(false);
    }
  };

  // Remove condition
  const handleRemoveCondition = (index) => {
    const updatedConditions = conditions.filter((_, i) => i !== index);
    updateNodeData(id, { ...data, conditions: updatedConditions });
  };

  return (
    <div className="logic-gate-node">
      {/* Input handle (left side) */}
      <Handle
        type="target"
        position={Position.Left}
        className={`w-3 h-3 !bg-${color}-500`}
      />

      <Card className={`
        w-64
        ${selected ? `ring-2 ring-${color}-500 shadow-lg` : 'shadow-md'}
        transition-all duration-200
        hover:shadow-xl
        bg-gradient-to-br from-${color}-500/10 to-${color}-600/5
        border-${color}-500/20
      `}>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className={`text-base font-semibold text-${color}-100 flex items-center gap-2`}>
              {isAND ? '◆' : '◇'}
              <Button
                size="sm"
                onClick={handleToggleGateType}
                className={`h-6 px-2 text-xs bg-${color}-600 hover:bg-${color}-700 no-drag`}
              >
                {gateType}
              </Button>
              Gate
            </CardTitle>
            <Badge variant="outline" className={`text-xs border-${color}-500/50 text-${color}-300`}>
              {conditions.length}
            </Badge>
          </div>
        </CardHeader>

        <CardContent className="space-y-2">
          {/* Editable Gate Name */}
          {isEditingName ? (
            <Input
              value={editedName}
              onChange={(e) => setEditedName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSaveName();
                if (e.key === 'Escape') setIsEditingName(false);
              }}
              placeholder="Gate name..."
              autoFocus
              className="text-sm font-medium bg-slate-800 border-slate-700 text-slate-200 no-drag"
            />
          ) : (
            <div
              className="flex items-center gap-2 cursor-pointer no-drag"
              onClick={() => {
                setEditedName(name || '');
                setIsEditingName(true);
              }}
            >
              {name ? (
                <div className="text-sm font-medium text-slate-200">{name}</div>
              ) : (
                <div className="text-xs text-slate-500 italic">Add name...</div>
              )}
              <Edit2 className="w-3 h-3 text-slate-500 hover:text-slate-300" />
            </div>
          )}

          {/* Conditions List */}
          <div className="space-y-1">
            {conditions.map((condition, index) => (
              <div
                key={index}
                className="flex items-center justify-between text-xs bg-slate-800/50 rounded px-2 py-1 border border-slate-700/50 no-drag"
              >
                <div className="flex-1">
                  <span className="text-slate-300">{condition.field}</span>
                  <span className="text-slate-500 mx-1">{condition.operator}</span>
                  <span className="text-purple-300">{String(condition.value)}</span>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => handleRemoveCondition(index)}
                  className="h-4 w-4 p-0 text-red-400 hover:text-red-300"
                >
                  <X className="w-3 h-3" />
                </Button>
              </div>
            ))}
          </div>

          {/* Add Condition */}
          {isAddingCondition ? (
            <div className="space-y-2 p-2 bg-slate-800/30 rounded border border-slate-700/50 no-drag">
              <Input
                placeholder="Field name..."
                value={newCondition.field}
                onChange={(e) => setNewCondition({ ...newCondition, field: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <Select
                value={newCondition.operator}
                onValueChange={(val) => setNewCondition({ ...newCondition, operator: val })}
              >
                <SelectTrigger className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  <SelectItem value="equals" className="text-slate-200">Equals</SelectItem>
                  <SelectItem value="not_equals" className="text-slate-200">Not Equals</SelectItem>
                  <SelectItem value="greater_than" className="text-slate-200">Greater Than</SelectItem>
                  <SelectItem value="less_than" className="text-slate-200">Less Than</SelectItem>
                  <SelectItem value="contains" className="text-slate-200">Contains</SelectItem>
                </SelectContent>
              </Select>
              <Input
                placeholder="Value..."
                value={newCondition.value}
                onChange={(e) => setNewCondition({ ...newCondition, value: e.target.value })}
                className="h-7 text-xs bg-slate-800 border-slate-700 text-slate-200"
              />
              <div className="flex gap-2">
                <Button
                  size="sm"
                  onClick={handleAddCondition}
                  className="flex-1 h-6 text-xs bg-green-600 hover:bg-green-700"
                >
                  Add
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setIsAddingCondition(false);
                    setNewCondition({ field: '', operator: 'equals', value: '' });
                  }}
                  className="flex-1 h-6 text-xs"
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setIsAddingCondition(true)}
              className={`w-full h-7 text-xs border-${color}-500/30 text-${color}-300 hover:bg-${color}-500/10 no-drag`}
            >
              <Plus className="w-3 h-3 mr-1" />
              Add Condition
            </Button>
          )}

          {/* Gate Type Info */}
          <div className="text-xs text-slate-400 pt-1 border-t border-slate-700/50">
            {isAND ? 'All must be true' : 'Any can be true'}
          </div>
        </CardContent>
      </Card>

      {/* Output handles (right side) - two for branching */}
      <Handle
        type="source"
        position={Position.Right}
        id="true"
        style={{ top: '35%' }}
        className={`w-3 h-3 !bg-green-500`}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="false"
        style={{ top: '65%' }}
        className={`w-3 h-3 !bg-red-500`}
      />
    </div>
  );
};

export default LogicGateNode;
